import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output, Renderer2
} from '@angular/core';
import {animationFrameScheduler, Subscription} from 'rxjs';

import {GetRegion2Css, GetRegion2Name, LinkInfo, WORD_LIST_MEANING} from '../../defines/client.const';
import {Word} from "../../models/word.model";
import {SimpleDialect} from '../../models/dialect.model';
import {VoiceService} from '../../services/voice/voice.service';
import {TotalLinkInfo, TotalLinkParam, TotalService} from '../../services/total/total.service';
import {clickOutsideLinkMixin} from "../../mixins/click-outside.mixin";
import {GlobalEventService} from "../../services/global-event/global-event.service";

const MAX_POPUP_ITEM = 3;
const POPUP_HEIGHT_DELTA = 60;


@Component({
  selector: 'app-link-popup',
  templateUrl: './link-popup.component.html',
})
export class LinkPopupComponent extends clickOutsideLinkMixin implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class') hostClass = 'link-popup popup-connect type-shadow';
  // @HostBinding('style.position') stylePosition = 'fixed';
  // @HostBinding('style.top') styleTop = '0';
  // @HostBinding('style.left') styleLeft = '0';
  @Input() linkInfo?: LinkInfo;
  @Output() closePopup = new EventEmitter<LinkInfo | null>();
  @Output() viewInit = new EventEmitter<HTMLElement>();

  protected readonly GetRegion2Css = GetRegion2Css;
  protected readonly GetRegion2Name = GetRegion2Name;

  protected countPopupItems = MAX_POPUP_ITEM;
  private load$: Subscription | null = null;

  public get IsLoading(): boolean {
    return this.load$ !== null;
  }

  private words?: Array<Word>;
  private dialects?: Array<SimpleDialect>;

  /**
   * 팝업창에 표시할 단어 목록 (최대 MAX_POPUP_ITEM 개)
   * @protected
   */
  public get listWord(): Array<Word> {
    return (this.words ?? []).slice(0, this.countPopupItems);
  }

  /**
   * 팝업창에 표시할 지방어 목록 (단어 포함, 최대 MAX_POPUP_ITEM 개)
   * @protected
   */
  public get listDialect(): Array<SimpleDialect> {
    const lenWord = this.words?.length ?? 0;
    const lenDialect = Math.max(0, Math.min((this.dialects?.length ?? 0), this.countPopupItems - lenWord));
    return (this.dialects ?? []).slice(0, lenDialect);
  }

  /**
   * 팝업창에 표시되지 않은 단어의 갯수
   * @private
   */
  private get leftWordCount(): number {
    return Math.max((this.words?.length ?? 0) - this.countPopupItems, 0);
  }

  /**
   * 팝업창에 표시되지 않은 지방어의 갯수
   * @private
   */
  private get leftDialectCount(): number {
    return (this.dialects?.length ?? 0) - this.listDialect.length - this.listWord.length;
  }

  public getMoreTxt(): string | null {
    return !this.isSingleLink ? this.getMoreLinkTxt() : this.getMoreWordMeaningsTxt();
  }

  /**
   * 표히된 연결 항목 이외에, 추가로 연결된 항목이 있는지 표시하는 문구
   */
  private getMoreLinkTxt(): string | null {
    let moreTxt: string | null = null;
    if (this.leftWordCount > 0 && this.leftDialectCount > 0) {
      moreTxt = `${this.leftWordCount}개의 단어, ${this.leftDialectCount}개의 우리말이 있습니다.`;
    }
    else if (this.leftWordCount > 0) {
      moreTxt = `${this.leftWordCount}개의 단어가 더 있습니다.`;
    }
    else if (this.leftDialectCount > 0) {
      moreTxt = `${this.leftDialectCount}개의 우리말이 더 있습니다.`;
    }
    return moreTxt;
  }

  /**
   * 연결된 항목이 하나인 경우, 그 한 항목에 대해서 추가 뜻이 있는지 표시
   */
  private getMoreWordMeaningsTxt(): string | null {
    if (this.isSingleLink && this.words?.length === 1) {
      const numMoreMeanings = Math.max(0, this.words[0].meaning.length - WORD_LIST_MEANING - 1); // -1 = 기본 뜻의 갯수는 뺀다.
      return numMoreMeanings > 0 ? '(' + numMoreMeanings + '개의 뜻이 더 있습니다)' : '';
    }
    else {
      return null;
    }
  }

  /**
   * 링크에 연결된 항목이 하나인지 여부
   * @protected
   */
  public get isSingleLink(): boolean {
    return ((this.words?.length ?? 0) + (this.dialects?.length ?? 0)) === 1;
  }

  constructor(
    public elementRef: ElementRef,
    public renderer: Renderer2,
    public globalEventSvc: GlobalEventService,
    private cdRef: ChangeDetectorRef,
    private totalSvc: TotalService,
    private voice: VoiceService,
  ) {
    super(elementRef, renderer, globalEventSvc);
  }

  ngOnInit(): void {
    // const parent: HTMLElement = this.elementRef.nativeElement.parentElement;

    // 상위 position: relative (또는 static이 아닌) 인 요소 찾기
    // const offsetParent = this.elementRef.nativeElement.offsetParent as HTMLElement;
    // console.warn('LinkPopupComponent.ngOnInit: offsetParent is null', this.elementRef, this.elementRef.nativeElement);
    // if (!offsetParent) {
    //   throw new Error('LinkPopupComponent.ngOnInit: offsetParent is null');
    // }

    // const {height} = getContentSize(offsetParent);
    // this.styleTop = `${height + 7}px`;

    // console.warn('LinkPopupComponent.ngOnInit', this.styleTop, parent.offsetHeight, parent);

    // 소리 재생 중단.
    this.voice.Stop();

    if ((this.linkInfo?.wordIds?.length ?? 0) > 0 || (this.linkInfo?.dialectIds?.length ?? 0) > 0) {
      const param: TotalLinkParam = {
        ...(this.linkInfo?.wordIds ? {wordIds: this.linkInfo.wordIds} : {}),
        ...(this.linkInfo?.dialectIds ? {dialectIds: this.linkInfo.dialectIds} : {}),
      };
      this.load$ = this.totalSvc.GetLinkInfo(param)
        .subscribe((linkInfo: TotalLinkInfo) => {
          console.log('LinkPopupComponent.ngOnInit.linkInfo', linkInfo);

          if ((linkInfo.word?.length ?? 0) > 0) {
            this.words = linkInfo.word;
          }

          if ((linkInfo.dialect?.length ?? 0) > 0) {
            this.dialects = linkInfo.dialect;
          }

          this.load$ = null;

          /**
           * 상위 컴포넌트에서 ChangeDetectionStrategy.OnPush을 지정한 경우에 대비해서 추가.
           * link-content 에서는 내부 초기화가 되면 곤란하므로 ChangeDetectionStrategy.OnPush을 지정한다.
           */
          this.cdRef.detectChanges();
        });
    }

    // mixin을 이용해서 팝업창 외부를 클릭했는지 체크한다.
    this.coInit(() => this.closePopup.emit(null));
  }

  ngAfterViewInit(): void {
    // this.viewInit.emit(this.elementRef.nativeElement.firstChild);
  }

  ngOnDestroy(): void {
    this.coDestroy();

    // 로딩 하고 있으면, 로딩을 중단한다.
    if (this.load$) {
      this.load$.unsubscribe();
      this.load$ = null;
    }

    // 소리 재생 중단.
    this.voice.Stop();
  }

  protected onCloseWord(wordId: string | null ): void {
    if (wordId) {
      const linkInfo: LinkInfo = {
        wordIds: [wordId],
      };
      this.closePopup.emit(linkInfo);
    }
    else {
      this.closePopup.emit(null);
    }
  }

  protected onCloseDialect(dialectId: string | null): void {
    if (dialectId) {
      const linkInfo: LinkInfo = {
        dialectIds: [dialectId],
      };
      this.closePopup.emit(linkInfo);
    }
    else {
      this.closePopup.emit(null);
    }
  }

  public onCloseMore($event: MouseEvent): void {
    console.warn('LinkPopupComponent.onCloseMore');

    $event.stopPropagation();
    $event.preventDefault();

    this.closePopup.emit(this.linkInfo);
  }


  /**
   * 상위 요소로 이벤트 전파를 막는다.
   * @param event
   */
  onVoid(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // this.globalEventSvc.Click(event);
  }

  onItemAfterViewInit() {
    const componentHeight = this.elementRef.nativeElement.offsetHeight;
    const windowHeight = window.innerHeight;

    console.log('LinkPopupComponent.onItemAfterViewInit', componentHeight, windowHeight, this.elementRef.nativeElement);

    if (componentHeight > windowHeight - POPUP_HEIGHT_DELTA) {
      console.warn('remove display', componentHeight, windowHeight - POPUP_HEIGHT_DELTA);
      animationFrameScheduler.schedule(() => {
        --this.countPopupItems;
        this.cdRef.detectChanges();
      });
    }
  }

  onClose($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    this.closePopup.emit(null);
  }
}
